<script setup lang="ts">
import SvgLoader from '@/utils/SvgLoader.vue';
import { computed, ref, StyleValue } from 'vue';
import { getRandomStr } from '@/businessRepository/idUtils';
import { i18n } from '@/i18n';

const props = defineProps<{ disabled?: boolean }>();

/** Emits */
const emits = defineEmits<{
  (e: 'select', option: 'download'): void;
}>();

/** Variables */
const t = i18n.t;
const containerId = getRandomStr(16);
const isMenuOpenedState = ref<boolean>(false);
const menuButtonPosition = ref({
  x: 0,
  y: 0,
});

/** Menu items */
const MENU_ITEMS = [
  {
    value: 'download' as const,
    icon: 'save_alt',
    text: 'ダウンロード',
  },
];

/** Function */
/**
 * ボタンクリック時orモーダル外をクリックした際の処理
 */
const onClickButton = () => {
  if (!isMenuOpenedState.value) {
    const buttonContent = document.getElementById(containerId);
    const bodyContent = document.getElementsByTagName('body')[0];
    if (!(buttonContent instanceof HTMLElement) || !(bodyContent instanceof HTMLElement)) return;
    menuButtonPosition.value = {
      x: buttonContent.offsetWidth,
      y: buttonContent.getBoundingClientRect().top,
    };
  }
  isMenuOpenedState.value = !isMenuOpenedState.value;
};

const dropdownMenuStyle = computed((): StyleValue => {
  return {
    top: `${menuButtonPosition.value.y + 20}px`,
    right: `${menuButtonPosition.value.x}px`,
  };
});

/**
 * 選択肢を選んだ際の処理
 * @param value - 選択した値
 */
const selectOption = (value: (typeof MENU_ITEMS)[number]['value']) => {
  isMenuOpenedState.value = false;
  emits('select', value);
};
</script>
<template>
  <div :id="containerId" class="table-row-menu__button__container" :class="{ 'not-allow-cursor': disabled }">
    <button
      class="table-row-menu__button"
      :class="{ 'table-row-menu__button--active': isMenuOpenedState, 'disabled-event': disabled }"
      @click="onClickButton"
    >
      <SvgLoader class="table-row-menu__icon" name="menuEllipsis" />
    </button>
  </div>
  <Teleport v-if="isMenuOpenedState" to="body">
    <div class="table-row-menu__bg" @click="onClickButton" />
    <div class="table-row-menu__dropdown" :style="dropdownMenuStyle">
      <div v-for="option in MENU_ITEMS" :key="option.value" class="table-row-menu__content">
        <button class="table-row-menu__option table-row-menu__option--active" @click="selectOption(option.value)">
          <SvgLoader class="table-row-menu__icon" :name="option.icon" />
          <div class="table-row-menu__option--text">{{ t(option.text) }}</div>
        </button>
      </div>
    </div>
  </Teleport>
</template>
<style lang="scss" scoped>
.table-row-menu {
  &__button {
    border-radius: 999px;
    &:hover,
    &--active {
      background-color: $semanticPrimarySubdued;
    }

    &__container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }

  &__icon {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    color: $textSecondary;
  }

  &__bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 11;
  }

  &__dropdown {
    position: fixed;
    width: fit-content;
    height: min-content;
    background-color: white;
    z-index: 12;
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
    box-shadow: 0px 0px 22px 0px #80808066;
    overflow: scroll;
    border-radius: 4px;
    border: 1px solid $borderDefault;
  }

  &__option {
    display: grid;
    grid-template-columns: min-content auto;
    gap: 4px;
    width: 100%;
    height: 36px;
    padding: 8px;
    align-items: center;
    &--active:hover {
      background-color: $semanticPrimarySubdued;
    }
    &--disabled {
      background-color: $textDisable;
    }

    &--text {
      display: grid;
      align-items: center;
      justify-content: left;
      font-size: 14px;
      font-weight: 400;
      text-align: left;
    }
  }
}

.not-allow-cursor {
  cursor: not-allowed;
}

.disabled-event {
  pointer-events: none;
}
</style>
